import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaGift,
  FaPlus,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaSpinner,
  FaStore,
  FaCheck,
  FaTimes,
  FaInfoCircle,
} from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import ModalPopup from '../CommonComponents/ModalPopup';
import {
  getBranchNamesForUser,
  getCategories,
  getPosOfferCatalog,
  listPosOffers,
  createPosOffer,
  updatePosOffer,
  deletePosOffer,
  getPosBillingProducts,
} from '../../services/ClientService';

const FREE_ITEM_TRIGGERS = [
  { value: 'cart_value', label: 'Current cart value ≥ ₹X' },
  { value: 'past_spend', label: 'Past bill spend ≥ ₹X' },
  { value: 'spend_with_cart', label: 'Past bills + this cart ≥ ₹X' },
  { value: 'order_count', label: 'N past orders this period' },
  { value: 'first_purchase', label: 'First purchase (welcome gift)' },
  { value: 'nth_visit', label: 'Every Nth visit' },
  { value: 'visit_streak', label: 'Visit streak (N distinct days)' },
  { value: 'always', label: 'Always (optional min cart)' },
];

const optionLabelOf = (option) => (
  option?.weight?.weightLabel
  || option?.weightLabel
  || (option?.weight?.weightValue
    ? `${option.weight.weightValue}${option.weight.metricLabel || ''}`
    : 'Option')
);

const PERIODS = [
  { value: 'calendar_month', label: 'This calendar month' },
  { value: 'today', label: 'Today' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'next_7d', label: 'Next 7 days' },
  { value: 'next_30d', label: 'Next 30 days' },
  { value: 'custom_days', label: 'Custom days' },
];

/** How long a forward-looking offer stays active from save date */
const validityFromPeriod = (period, customDays) => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  let days = null;
  if (period === 'next_7d') days = 7;
  else if (period === 'next_30d') days = 30;
  else if (period === 'custom_days') days = Math.max(1, Number(customDays) || 1);
  else return { validFrom: null, validTo: null, customDays: null };
  end.setDate(end.getDate() + (days - 1));
  end.setHours(23, 59, 59, 999);
  return { validFrom: start.toISOString(), validTo: end.toISOString(), customDays: days };
};

const periodLabel = (offer) => {
  if (offer?.period === 'custom_days') {
    return `Custom ${offer.customDays || '?'} days`;
  }
  if (offer?.period === 'rolling_7d') return 'Last 7 days (legacy)';
  if (offer?.period === 'rolling_30d') return 'Last 30 days (legacy)';
  return PERIODS.find((p) => p.value === offer?.period)?.label || offer?.period || '—';
};

const ALL_BRANCHES = 'all';

/** Small ⓘ that shows a plain-language tip on hover / focus / tap */
const InfoTip = ({ text, placement = 'bottom', align = 'start', width = 280 }) => {
  const isTop = placement === 'top';
  const isEnd = align === 'end';
  const isCenter = align === 'center';
  return (
    <span className="relative inline-flex items-center group align-middle ml-1">
      <button
        type="button"
        className="text-slate-400 hover:text-teal-600 focus:text-teal-600 focus:outline-none"
        aria-label="More information"
        tabIndex={0}
      >
        <FaInfoCircle size={13} />
      </button>
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-[70] rounded-lg bg-slate-900 text-white text-[11px] leading-relaxed px-3 py-2.5 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-150 whitespace-pre-line text-left font-normal break-words ${
          isTop ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]'
        } ${isEnd ? 'right-0' : isCenter ? 'left-1/2' : 'left-0'}`}
        style={{
          width,
          minWidth: width,
          maxWidth: `min(${width}px, calc(100vw - 2.5rem))`,
          ...(isCenter ? { transform: 'translateX(-50%)' } : null),
        }}
      >
        {text}
        <span
          className={`absolute border-4 border-transparent ${
            isTop ? 'top-full border-t-slate-900' : 'bottom-full border-b-slate-900'
          } ${isEnd ? 'right-2' : isCenter ? 'left-1/2 -translate-x-1/2' : 'left-2'}`}
        />
      </span>
    </span>
  );
};

const FieldLabel = ({
  children,
  tip,
  tipPlacement = 'bottom',
  tipAlign = 'start',
  tipWidth = 280,
  className = '',
}) => (
  <span className={`inline-flex items-center text-xs font-medium text-slate-600 ${className}`}>
    {children}
    {tip ? (
      <InfoTip
        text={tip}
        placement={tipPlacement}
        align={tipAlign}
        width={tipWidth}
      />
    ) : null}
  </span>
);

const TIPS = {
  period: 'When the offer runs / how bills are counted.\n• Lifetime / calendar month / today = counting window for loyalty streaks.\n• Next 7 / 30 / custom days = offer is active only until that end date (great for UPI boost, cart slabs, bundles).',
  priority: 'If TWO offers can apply on the same bill, which one wins?\n• Smaller number = more important (1 beats 10).\n• Leave 10 if you only have one offer.\n• Example: Loyalty treat priority 5, Happy Hours priority 15 → loyalty wins when both qualify.',
  stackCoupon: 'If ON: loyalty discount + coupon can both apply on same bill.\nIf OFF (recommended): customer gets either coupon OR this offer, not both.',
  oneTime: 'If ON: each customer can redeem this offer only once (lifetime), on any rule type.\nAfter they get it once, it will not apply again even if conditions match.\nNeeds the customer\'s mobile so we can check past bills.',
};

/** Detailed tip text for each rule type (shown next to Rule type) */
const RULE_TYPE_TIPS = {
  order_count_milestone:
    'Order streak reward\nCustomer places N POS bills in the Period → reward unlocks on a following bill.\n• Needs customer mobile.\n• Good for kirana / FMCG “shop 5 times this month”.\n• Set Orders needed + reward below.',
  spend_milestone:
    'Spend & unlock\nPast bills + THIS cart must reach ₹X in the Period.\n• This bill must ALSO be ≥ ₹X to redeem (a ₹90 cart cannot redeem a ₹500 offer).\n• Needs mobile.\n• Set Minimum spend + reward.',
  past_spend_milestone:
    'Past bill spend reward\nPast POS bills must already total ₹X (this cart does not count toward the target).\n• This bill must still be ≥ ₹X to redeem.\n• Needs mobile.\n• Use for “big past spend → redeem on a proper bill”.',
  nth_visit:
    'Every Nth visit\nLike a punch card: every 3rd / 4th / 5th visit gets the treat (visits 4, 8, 12…).\n• Needs mobile.\n• Use Period = Lifetime for classic punch-card.\n• “Counts at this branch / all branches” still applies.',
  category_loyalty:
    'Category / subcategory loyalty\nOnly bills that include items from the chosen category/subcategory count toward N orders.\n• Needs mobile + product scope.\n• Example: 5 Atta bills → ₹100 off.',
  cart_value_slab:
    'Cart value slab\nInstant discount when THIS cart’s subtotal crosses ₹X — no past history needed.\n• Use Period = Next 7 / 30 / custom days for campaign length.\n• Optional: one-time per customer + mobile.',
  happy_hours:
    'Happy hours\nDiscount only during the time window you set (e.g. 4–7 PM).\n• Great for bakery clearance / lunch rush.\n• Pair with Next 7 / 30 / custom days so the campaign ends automatically.',
  first_purchase:
    'Welcome / first bill\nApplies only when this mobile has never billed before (first POS bill).\n• Needs mobile.\n• Perfect acquisition / welcome Flat or %.\n• Usually Period = Lifetime.',
  payment_method_boost:
    'UPI / Cash / Card boost\nExtra off when the cashier selects a preferred payment method (UPI / Cash / Card).\n• Can require a min cart value.\n• Use Next 7 / 30 / custom days to run a short UPI drive.\n• Tick payment methods below.',
  qty_bundle:
    'Quantity bundle\nBuy N units in a category/subcategory on THIS cart → Flat / % off.\n• No past visits needed.\n• Example: 5 soap bars → 10% off.\n• Set Product scope + qty.',
  visit_streak:
    'Visit streak\nCustomer must shop on N different calendar days in the Period (not just N bills same day).\n• Needs mobile.\n• Builds daily habit (kirana / bakery).\n• Today’s visit counts toward the streak.',
  free_items:
    'Free gift items\nWhen the trigger matches, selected product options are auto-added on Merchant Billing at ₹0.\n• Pick gifts by search / category / subcategory.\n• Bill shows “items worth ₹X free”.\n• Choose trigger: cart value, past spend, visits, first bill, etc.',
};

const ruleTypeTip = (ruleType) => (
  RULE_TYPE_TIPS[ruleType]
  || 'Pick how this offer unlocks on Merchant Billing. Tip text updates when you change the rule.'
);

const emptyForm = (catalogItem, branchId) => ({
  name: catalogItem?.label || '',
  description: catalogItem?.blurb || '',
  ruleType: catalogItem?.ruleType || 'order_count_milestone',
  isActive: true,
  priority: 10,
  period: catalogItem?.defaults?.period || 'calendar_month',
  customDays: catalogItem?.defaults?.customDays || 14,
  scope: {
    type: catalogItem?.defaults?.scope?.type || 'all',
    categoryIds: [],
    subcategoryIds: [],
  },
  thresholds: {
    triggerType: 'cart_value',
    minCartValue: 499,
    minSpend: 0,
    orderCount: 1,
    ...(catalogItem?.defaults?.thresholds || {}),
  },
  reward: { ...(catalogItem?.defaults?.reward || { type: 'flat', value: 100, maxDiscount: null }) },
  freeGiftItems: [...(catalogItem?.defaults?.freeGiftItems || [])],
  paymentMethods: [...(catalogItem?.defaults?.paymentMethods || [])],
  happyHours: catalogItem?.defaults?.happyHours
    ? { ...catalogItem.defaults.happyHours }
    : { start: '16:00', end: '19:00', days: [0, 1, 2, 3, 4, 5, 6] },
  stackWithCoupon: false,
  oneTimePerCustomer: false,
  messages: {},
  industries: catalogItem?.industries || [],
  // 'all' = every branch; otherwise a specific branch id
  branchId: !branchId || branchId === ALL_BRANCHES ? ALL_BRANCHES : String(branchId),
  allBranches: !branchId || branchId === ALL_BRANCHES,
  countScope: !branchId || branchId === ALL_BRANCHES ? 'all_branches' : 'offer_branch',
});

const formatReward = (reward, offer) => {
  if (reward?.type === 'free_items' || offer?.ruleType === 'free_items') {
    const n = (offer?.freeGiftItems || []).length;
    const worth = (offer?.freeGiftItems || []).reduce(
      (s, g) => s + (Number(g.catalogPrice) || 0) * (Number(g.quantity) || 1),
      0,
    );
    return n
      ? `${n} free gift${n > 1 ? 's' : ''}${worth > 0 ? ` · worth ₹${worth.toFixed(0)}` : ''}`
      : 'Free gifts (none selected)';
  }
  if (!reward) return '—';
  if (reward.type === 'percent') {
    return `${reward.value}% off${reward.maxDiscount ? ` up to ₹${reward.maxDiscount}` : ''}`;
  }
  return `Flat ₹${reward.value} off`;
};

const PosOffers = () => {
  const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || '{}'), []);

  const [branches, setBranches] = useState([]);
  const [canSwitchBranch, setCanSwitchBranch] = useState(true);
  const [filterBranchId, setFilterBranchId] = useState(ALL_BRANCHES);
  const [catalog, setCatalog] = useState([]);
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [offerPendingDelete, setOfferPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(() => emptyForm(null, ALL_BRANCHES));
  const [industryFilter, setIndustryFilter] = useState('All');
  const [giftSearch, setGiftSearch] = useState('');
  const [giftCategoryId, setGiftCategoryId] = useState('');
  const [giftSubcategoryId, setGiftSubcategoryId] = useState('');
  const [giftProducts, setGiftProducts] = useState([]);
  const [giftLoading, setGiftLoading] = useState(false);
  const [debouncedGiftSearch, setDebouncedGiftSearch] = useState('');

  const showSnack = (message, type = 'success') => {
    setSnack({ message, type });
  };

  const branchLabel = useCallback((id) => {
    if (!id || id === ALL_BRANCHES) return 'All branches';
    const b = branches.find((x) => String(x._id) === String(id));
    return b?.branchName || b?.name || 'Branch';
  }, [branches]);

  useEffect(() => {
    if (!snack) return undefined;
    const t = setTimeout(() => setSnack(null), 3200);
    return () => clearTimeout(t);
  }, [snack]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedGiftSearch(giftSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [giftSearch]);

  useEffect(() => {
    if (!showForm || form.ruleType !== 'free_items') return undefined;
    let cancelled = false;
    (async () => {
      setGiftLoading(true);
      try {
        const res = await getPosBillingProducts({
          searchTerm: debouncedGiftSearch || undefined,
          categoryId: giftSubcategoryId || giftCategoryId || undefined,
          skip: 0,
          limit: 30,
        });
        if (!cancelled) {
          setGiftProducts(Array.isArray(res?.data) ? res.data : (res?.products || []));
        }
      } catch {
        if (!cancelled) setGiftProducts([]);
      } finally {
        if (!cancelled) setGiftLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showForm, form.ruleType, debouncedGiftSearch, giftCategoryId, giftSubcategoryId]);

  useEffect(() => {
    (async () => {
      try {
        const [brRes, catRes] = await Promise.all([
          getBranchNamesForUser(),
          getPosOfferCatalog({}),
        ]);
        if (brRes?.success && Array.isArray(brRes.data)) {
          const list = brRes.data.filter((b) => !b.isWarehouse);
          setBranches(list);
          const canSwitch = brRes.canSwitchBranch !== false && list.length > 1;
          setCanSwitchBranch(canSwitch);

          if (!canSwitch && list.length === 1) {
            setFilterBranchId(String(list[0]._id));
          } else {
            // Prefer currently selected HQ branch if present; else All
            const preferred = userData.selectedBranch
              || userData._id
              || null;
            const match = preferred && list.find((b) => String(b._id) === String(preferred));
            setFilterBranchId(match ? String(match._id) : ALL_BRANCHES);
          }
        }
        if (catRes?.success) setCatalog(catRes.data || []);
      } catch (e) {
        showSnack(e.message || 'Failed to load setup data', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOffers = useCallback(async () => {
    if (!filterBranchId) return;
    setLoading(true);
    try {
      const [offRes, catRes] = await Promise.all([
        listPosOffers({ branchId: filterBranchId, includeInactive: true }),
        getCategories({ includeSubcategories: true }),
      ]);
      if (offRes?.success) setOffers(offRes.data || []);
      else showSnack(offRes?.message || 'Failed to load offers', 'error');
      if (catRes?.success || catRes?.data) {
        const cats = catRes.data || catRes.categories || [];
        setCategories(Array.isArray(cats) ? cats.filter((c) => !c.parentCategoryId) : []);
      }
    } catch (e) {
      showSnack(e.message || 'Failed to load offers', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterBranchId]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const industries = useMemo(() => {
    const set = new Set(['All']);
    catalog.forEach((c) => (c.industries || []).forEach((i) => set.add(i)));
    return Array.from(set);
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    if (industryFilter === 'All') return catalog;
    return catalog.filter((c) => (c.industries || []).includes(industryFilter));
  }, [catalog, industryFilter]);

  const parentCategories = categories;
  const selectedParent = parentCategories.find(
    (c) => String(c._id) === String(form.scope?.categoryIds?.[0]),
  );
  const subcategories = Array.isArray(selectedParent?.subcategories)
    ? selectedParent.subcategories
    : [];

  const defaultFormBranch = filterBranchId === ALL_BRANCHES
    ? (branches.find((b) => !b.masterAccount)?._id
      ? String(branches.find((b) => !b.masterAccount)._id)
      : ALL_BRANCHES)
    : filterBranchId;

  const openCreateFromCatalog = (item) => {
    setEditingId(null);
    setForm(emptyForm(item, defaultFormBranch));
    setShowForm(true);
  };

  const openEdit = (offer) => {
    setEditingId(offer._id);
    const branchInList = offer.branchId
      && branches.some((b) => String(b._id) === String(offer.branchId));
    const isAll = offer.allBranches === true
      || !offer.branchId
      || offer.branchId === 'all'
      || !branchInList;
    setForm({
      name: offer.name || '',
      description: offer.description || '',
      ruleType: offer.ruleType,
      isActive: offer.isActive !== false,
      priority: offer.priority || 10,
      period: offer.period === 'rolling_7d'
        ? 'next_7d'
        : offer.period === 'rolling_30d'
          ? 'next_30d'
          : (offer.period || 'calendar_month'),
      customDays: offer.customDays || (
        offer.period === 'custom_days' ? 14
          : offer.period === 'rolling_7d' || offer.period === 'next_7d' ? 7
            : offer.period === 'rolling_30d' || offer.period === 'next_30d' ? 30
              : 14
      ),
      validFrom: offer.validFrom || null,
      validTo: offer.validTo || null,
      scope: offer.scope || { type: 'all', categoryIds: [], subcategoryIds: [] },
      thresholds: {
        triggerType: 'cart_value',
        minCartValue: 499,
        minSpend: 0,
        orderCount: 1,
        ...(offer.thresholds || {}),
      },
      reward: offer.reward || { type: 'flat', value: 100 },
      freeGiftItems: Array.isArray(offer.freeGiftItems) ? offer.freeGiftItems : [],
      paymentMethods: offer.paymentMethods || [],
      happyHours: offer.happyHours || { start: '16:00', end: '19:00', days: [0, 1, 2, 3, 4, 5, 6] },
      stackWithCoupon: !!offer.stackWithCoupon,
      oneTimePerCustomer: !!offer.oneTimePerCustomer,
      messages: offer.messages || {},
      industries: offer.industries || [],
      branchId: isAll ? ALL_BRANCHES : String(offer.branchId),
      allBranches: isAll,
      countScope: offer.countScope === 'offer_branch' || offer.countScope === 'all_branches'
        ? offer.countScope
        : (isAll ? 'all_branches' : 'offer_branch'),
    });
    setShowForm(true);
  };

  const patchForm = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      if (path.includes('.')) {
        const [a, b] = path.split('.');
        next[a] = { ...(next[a] || {}), [b]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      showSnack('Offer name is required', 'error');
      return;
    }
    const isAll = form.branchId === ALL_BRANCHES || form.allBranches === true;
    if (!isAll && !form.branchId) {
      showSnack('Select a branch or All branches', 'error');
      return;
    }
    if (form.period === 'custom_days' && !(Number(form.customDays) > 0)) {
      showSnack('Enter custom days (at least 1)', 'error');
      return;
    }
    if (form.ruleType === 'free_items' && !(form.freeGiftItems || []).length) {
      showSnack('Select at least one free gift product option', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        allBranches: isAll,
        countScope: form.countScope === 'offer_branch' || form.countScope === 'all_branches'
          ? form.countScope
          : (isAll ? 'all_branches' : 'offer_branch'),
        branchId: isAll ? ALL_BRANCHES : form.branchId,
        customDays: form.period === 'custom_days'
          ? Number(form.customDays)
          : form.period === 'next_7d'
            ? 7
            : form.period === 'next_30d'
              ? 30
              : null,
        reward: form.ruleType === 'free_items'
          ? { type: 'free_items', value: 0, maxDiscount: null }
          : form.reward,
        freeGiftItems: form.ruleType === 'free_items' ? (form.freeGiftItems || []) : [],
      };
      let res;
      if (editingId) {
        res = await updatePosOffer({ offerId: editingId, ...payload });
      } else {
        res = await createPosOffer(payload);
      }
      if (res?.success) {
        showSnack(editingId ? 'Offer updated' : 'Offer created');
        setShowForm(false);
        setEditingId(null);
        await loadOffers();
      } else {
        showSnack(res?.message || 'Save failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (offer) => {
    try {
      const res = await updatePosOffer({
        offerId: offer._id,
        isActive: !offer.isActive,
      });
      if (res?.success) {
        showSnack(offer.isActive ? 'Offer paused' : 'Offer activated');
        await loadOffers();
      } else showSnack(res?.message || 'Update failed', 'error');
    } catch (e) {
      showSnack(e.message || 'Update failed', 'error');
    }
  };

  const handleDeleteClick = (offer) => {
    setOfferPendingDelete(offer);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
    setOfferPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!offerPendingDelete?._id) return;
    setDeleting(true);
    try {
      const res = await deletePosOffer({ offerId: offerPendingDelete._id });
      if (res?.success) {
        showSnack('Offer deleted');
        setShowDeleteConfirm(false);
        setOfferPendingDelete(null);
        await loadOffers();
      } else showSnack(res?.message || 'Delete failed', 'error');
    } catch (e) {
      showSnack(e.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const isFreeItems = form.ruleType === 'free_items';
  const freeTrigger = form.thresholds?.triggerType || 'cart_value';
  const needsScope = !isFreeItems && (
    ['category_loyalty', 'qty_bundle'].includes(form.ruleType)
    || form.scope?.type === 'category'
    || form.scope?.type === 'subcategory'
  );
  const needsOrderCount = ['order_count_milestone', 'category_loyalty', 'nth_visit', 'visit_streak'].includes(form.ruleType)
    || (isFreeItems && ['order_count', 'nth_visit', 'visit_streak'].includes(freeTrigger));
  const needsMinSpend = form.ruleType === 'spend_milestone'
    || form.ruleType === 'past_spend_milestone'
    || (isFreeItems && ['past_spend', 'spend_with_cart'].includes(freeTrigger));
  const needsMinCart = ['cart_value_slab', 'payment_method_boost'].includes(form.ruleType)
    || (isFreeItems && ['cart_value', 'always'].includes(freeTrigger));
  const needsQty = form.ruleType === 'qty_bundle';
  const needsHappyHours = form.ruleType === 'happy_hours';
  const needsPaymentMethods = form.ruleType === 'payment_method_boost';

  const giftParent = parentCategories.find((c) => String(c._id) === String(giftCategoryId));
  const giftSubs = Array.isArray(giftParent?.subcategories) ? giftParent.subcategories : [];

  const toggleFreeGiftOption = (product, option) => {
    const productMongoId = String(product._id);
    const optionId = String(option._id);
    const exists = (form.freeGiftItems || []).some(
      (g) => String(g.productMongoId) === productMongoId && String(g.optionId) === optionId,
    );
    if (exists) {
      setForm((prev) => ({
        ...prev,
        freeGiftItems: (prev.freeGiftItems || []).filter(
          (g) => !(String(g.productMongoId) === productMongoId && String(g.optionId) === optionId),
        ),
      }));
      return;
    }
    const catalogPrice = Number(option.discountPrice || option.originalPrice || 0) || 0;
    setForm((prev) => ({
      ...prev,
      freeGiftItems: [
        ...(prev.freeGiftItems || []),
        {
          productMongoId,
          optionId,
          quantity: 1,
          productName: product.productName || 'Product',
          optionLabel: optionLabelOf(option),
          catalogPrice,
        },
      ],
    }));
  };

  const removeFreeGift = (productMongoId, optionId) => {
    setForm((prev) => ({
      ...prev,
      freeGiftItems: (prev.freeGiftItems || []).filter(
        (g) => !(String(g.productMongoId) === String(productMongoId) && String(g.optionId) === String(optionId)),
      ),
    }));
  };

  const patchFreeGiftQty = (productMongoId, optionId, quantity) => {
    setForm((prev) => ({
      ...prev,
      freeGiftItems: (prev.freeGiftItems || []).map((g) => (
        String(g.productMongoId) === String(productMongoId) && String(g.optionId) === String(optionId)
          ? { ...g, quantity: Math.max(1, Number(quantity) || 1) }
          : g
      )),
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-teal-700 mb-1">
              <FaGift size={22} />
              <span className="text-sm font-semibold tracking-wide uppercase">POS Loyalty</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">POS Offers</h1>
            <p className="text-slate-600 mt-1 max-w-2xl text-sm sm:text-base">
              Configure India-ready loyalty rules for Retail, FMCG, Bakery, Electronics & more.
              Rules apply automatically on Merchant Billing and print highlighted on the bill.
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex items-center gap-2 text-slate-700 font-medium shrink-0">
            <FaStore className="text-teal-600" />
            Filter by branch
          </div>
          <select
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white disabled:bg-slate-50"
            value={filterBranchId || ALL_BRANCHES}
            onChange={(e) => setFilterBranchId(e.target.value)}
            disabled={!canSwitchBranch && branches.length <= 1}
          >
            {canSwitchBranch && (
              <option value={ALL_BRANCHES}>All branches</option>
            )}
            {branches.map((b) => (
              <option key={String(b._id)} value={String(b._id)}>
                {b.branchName || b.name || String(b._id)}
                {b.masterAccount ? ' (HQ)' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => openCreateFromCatalog(catalog[0] || null)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700"
          >
            <FaPlus size={12} /> New offer
          </button>
        </div>

        {/* Rule templates */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-semibold text-slate-800">Start from a proven rule</h2>
            <div className="flex flex-wrap gap-2">
              {industries.map((ind) => (
                <button
                  key={ind}
                  type="button"
                  onClick={() => setIndustryFilter(ind)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    industryFilter === ind
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                  }`}
                >
                  {ind}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredCatalog.map((item) => (
              <button
                key={item.ruleType}
                type="button"
                onClick={() => openCreateFromCatalog(item)}
                className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-teal-400 hover:shadow-sm transition"
              >
                <div className="font-semibold text-slate-900">{item.label}</div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.blurb}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(item.industries || []).slice(0, 3).map((i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{i}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Active offers */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Configured offers ({offers.length})</h2>
            {loading && <FaSpinner className="animate-spin text-teal-600" />}
          </div>
          {!loading && offers.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">
              No offers for this branch yet. Pick a template above — e.g. “5 bills → Flat ₹100 on the 6th”.
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {offers.map((offer) => (
              <li key={String(offer._id)} className="px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{offer.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      offer.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {offer.isActive ? 'Active' : 'Paused'}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">
                      {offer.ruleType?.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      offer.allBranches === true
                        || offer.branchId == null
                        || offer.branchId === ''
                        || offer.branchId === 'all'
                        || !branches.some((b) => String(b._id) === String(offer.branchId))
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'bg-amber-50 text-amber-800'
                    }`}>
                      {offer.allBranches === true
                        || offer.branchId == null
                        || offer.branchId === ''
                        || offer.branchId === 'all'
                        || !branches.some((b) => String(b._id) === String(offer.branchId))
                        ? 'All branches'
                        : branchLabel(offer.branchId)}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      {(offer.countScope === 'offer_branch'
                        || (!offer.countScope && offer.allBranches !== true && offer.branchId))
                        ? 'Counts: this branch'
                        : 'Counts: all branches'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {formatReward(offer.reward, offer)}
                    {' · '}
                    {periodLabel(offer)}
                    {offer.validTo ? ` · till ${new Date(offer.validTo).toLocaleDateString()}` : ''}
                    {offer.thresholds?.orderCount ? ` · after ${offer.thresholds.orderCount} orders` : ''}
                    {offer.thresholds?.minSpend
                      ? ` · ${offer.ruleType === 'past_spend_milestone' ? 'past spend' : 'spend'} ₹${offer.thresholds.minSpend}`
                      : ''}
                    {offer.thresholds?.minCartValue ? ` · cart ≥ ₹${offer.thresholds.minCartValue}` : ''}
                  </p>
                  {offer.description && (
                    <p className="text-xs text-slate-400 mt-1 truncate">{offer.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleActive(offer)}
                    className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                    title={offer.isActive ? 'Pause' : 'Activate'}
                  >
                    {offer.isActive ? <FaToggleOn className="text-teal-600" size={22} /> : <FaToggleOff size={22} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(offer)}
                    className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(offer)}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50"
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Form drawer */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto overflow-x-hidden">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">
                {editingId ? 'Edit offer' : 'Create offer'}
              </h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg">
                <FaTimes />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Offer name</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => patchForm('name', e.target.value)}
                  placeholder="e.g. Every 4th visit ₹50 off"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-600">Offer available at *</span>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                  value={form.branchId || ALL_BRANCHES}
                  onChange={(e) => {
                    const v = e.target.value;
                    const isAll = v === ALL_BRANCHES;
                    setForm((prev) => ({
                      ...prev,
                      branchId: v,
                      allBranches: isAll,
                      countScope: isAll ? 'all_branches' : 'offer_branch',
                    }));
                  }}
                >
                  <option value={ALL_BRANCHES}>All branches</option>
                  {branches.map((b) => (
                    <option key={String(b._id)} value={String(b._id)}>
                      {b.branchName || b.name || String(b._id)}
                      {b.masterAccount ? ' (HQ)' : ''}
                    </option>
                  ))}
                </select>
              </label>

              <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                <span className="text-xs font-medium text-slate-600">How are visits / spend counted? *</span>
                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    className="mt-1"
                    name="countScope"
                    checked={(form.countScope || 'all_branches') === 'all_branches'}
                    onChange={() => patchForm('countScope', 'all_branches')}
                  />
                  <span>
                    <span className="font-medium">All branches</span>
                    <span className="block text-[11px] text-slate-500">
                      Add bills from every store together.
                    </span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 text-sm cursor-pointer ${
                  form.branchId === ALL_BRANCHES ? 'opacity-50' : 'text-slate-700'
                }`}>
                  <input
                    type="radio"
                    className="mt-1"
                    name="countScope"
                    disabled={form.branchId === ALL_BRANCHES}
                    checked={(form.countScope || 'all_branches') === 'offer_branch'}
                    onChange={() => patchForm('countScope', 'offer_branch')}
                  />
                  <span>
                    <span className="font-medium">Selected branch only</span>
                    <span className="block text-[11px] text-slate-500">
                      Count only this store’s bills.
                      {form.branchId === ALL_BRANCHES ? ' Pick one branch above to enable.' : ''}
                    </span>
                  </span>
                </label>
              </div>

              <label className="block">
                <FieldLabel
                  tip={ruleTypeTip(form.ruleType)}
                  tipPlacement="bottom"
                  tipAlign="start"
                  tipWidth={300}
                >
                  Rule type
                </FieldLabel>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.ruleType}
                  onChange={(e) => {
                    const item = catalog.find((c) => c.ruleType === e.target.value);
                    const next = emptyForm(item, form.branchId);
                    setForm({
                      ...next,
                      name: form.name || next.name,
                      description: form.description || next.description,
                      countScope: form.countScope,
                      allBranches: form.allBranches,
                      branchId: form.branchId,
                      stackWithCoupon: form.stackWithCoupon,
                      oneTimePerCustomer: form.oneTimePerCustomer,
                      isActive: form.isActive,
                      priority: form.priority,
                      period: form.period,
                      customDays: form.customDays,
                    });
                  }}
                  disabled={!!editingId}
                >
                  {catalog.map((c) => (
                    <option key={c.ruleType} value={c.ruleType}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-600">Description (for your team)</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  rows={2}
                  value={form.description}
                  onChange={(e) => patchForm('description', e.target.value)}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <FieldLabel tip={TIPS.period}>Period</FieldLabel>
                  <select
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={PERIODS.some((p) => p.value === form.period) ? form.period : 'calendar_month'}
                    onChange={(e) => {
                      const next = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        period: next,
                        customDays: next === 'next_7d'
                          ? 7
                          : next === 'next_30d'
                            ? 30
                            : next === 'custom_days'
                              ? (Number(prev.customDays) > 0 ? prev.customDays : 14)
                              : prev.customDays,
                        // clear so preview shows new window from today
                        validFrom: null,
                        validTo: null,
                      }));
                    }}
                  >
                    {PERIODS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <FieldLabel tip={TIPS.priority} tipPlacement="top" tipAlign="end">Priority</FieldLabel>
                  <input
                    type="number"
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.priority}
                    onChange={(e) => patchForm('priority', Number(e.target.value) || 10)}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Lower number = more important</p>
                </label>
              </div>

              {form.period === 'custom_days' && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Number of days</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.customDays ?? 14}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      customDays: Math.max(1, Number(e.target.value) || 1),
                      validFrom: null,
                      validTo: null,
                    }))}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Offer stays active for this many days from today (including today)
                  </p>
                </label>
              )}
              {['next_7d', 'next_30d', 'custom_days'].includes(form.period) && (() => {
                const preview = validityFromPeriod(form.period, form.customDays);
                const showTo = editingId && form.validTo && form.period
                  ? form.validTo
                  : preview.validTo;
                if (!showTo) return null;
                return (
                  <p className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
                    Active till {new Date(showTo).toLocaleDateString()}
                    {editingId
                      ? ' — changing period or custom days will reset this window from today'
                      : ''}
                  </p>
                );
              })()}

              {/* Thresholds */}
              {needsOrderCount && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">
                    {form.ruleType === 'nth_visit' || (isFreeItems && freeTrigger === 'nth_visit')
                      ? 'Every Nth visit'
                      : form.ruleType === 'visit_streak' || (isFreeItems && freeTrigger === 'visit_streak')
                        ? 'Distinct visit days'
                        : isFreeItems && freeTrigger === 'order_count'
                          ? 'Orders needed'
                          : 'Orders needed'}
                  </span>
                  <input
                    type="number"
                    min={1}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.thresholds?.orderCount ?? 5}
                    onChange={(e) => patchForm('thresholds.orderCount', Number(e.target.value) || 1)}
                  />
                </label>
              )}
              {needsMinSpend && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">
                    {form.ruleType === 'past_spend_milestone'
                      ? 'Minimum past bill spend (₹)'
                      : 'Minimum spend (₹)'}
                  </span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.thresholds?.minSpend ?? 5000}
                    onChange={(e) => patchForm('thresholds.minSpend', Number(e.target.value) || 0)}
                  />
                  {form.ruleType === 'past_spend_milestone' && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      Past bills must reach this amount; this cart must also be ≥ this amount to redeem
                    </p>
                  )}
                  {form.ruleType === 'spend_milestone' && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      Past + this cart must reach the target, and this bill must be ≥ this amount to redeem
                    </p>
                  )}
                </label>
              )}
              {needsMinCart && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Minimum cart value (₹)</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.thresholds?.minCartValue ?? 999}
                    onChange={(e) => patchForm('thresholds.minCartValue', Number(e.target.value) || 0)}
                  />
                </label>
              )}
              {needsQty && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Minimum quantity in scope</span>
                  <input
                    type="number"
                    min={1}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.thresholds?.qty ?? 5}
                    onChange={(e) => patchForm('thresholds.qty', Number(e.target.value) || 1)}
                  />
                </label>
              )}

              {isFreeItems && (
                <div className="border border-teal-200 rounded-lg p-3 space-y-3 bg-teal-50/40">
                  <FieldLabel tip={RULE_TYPE_TIPS.free_items} tipWidth={300}>
                    Free gift unlock condition
                  </FieldLabel>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    value={freeTrigger}
                    onChange={(e) => patchForm('thresholds.triggerType', e.target.value)}
                  >
                    {FREE_ITEM_TRIGGERS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Scope — not used for free-items gifts */}
              {!isFreeItems && (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <span className="text-xs font-medium text-slate-600">Product scope</span>
                  <select
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.scope?.type || 'all'}
                    onChange={(e) => patchForm('scope', {
                      type: e.target.value,
                      categoryIds: form.scope?.categoryIds || [],
                      subcategoryIds: form.scope?.subcategoryIds || [],
                    })}
                  >
                    <option value="all">All products</option>
                    <option value="category">Category</option>
                    <option value="subcategory">Subcategory</option>
                  </select>
                  {(form.scope?.type === 'category' || form.scope?.type === 'subcategory' || needsScope) && (
                    <>
                      <select
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        value={form.scope?.categoryIds?.[0] || ''}
                        onChange={(e) => patchForm('scope', {
                          ...form.scope,
                          type: form.scope?.type === 'all' ? 'category' : form.scope.type,
                          categoryIds: e.target.value ? [e.target.value] : [],
                          subcategoryIds: [],
                        })}
                      >
                        <option value="">Select category</option>
                        {parentCategories.map((c) => (
                          <option key={String(c._id)} value={String(c._id)}>{c.categoryName || c.name}</option>
                        ))}
                      </select>
                      {(form.scope?.type === 'subcategory') && (
                        <select
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                          value={form.scope?.subcategoryIds?.[0] || ''}
                          onChange={(e) => patchForm('scope', {
                            ...form.scope,
                            subcategoryIds: e.target.value ? [e.target.value] : [],
                          })}
                        >
                          <option value="">Select subcategory</option>
                          {subcategories.map((s) => (
                            <option key={String(s._id)} value={String(s._id)}>{s.categoryName || s.name}</option>
                          ))}
                        </select>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Reward — cash discount OR free gift picker */}
              {isFreeItems ? (
                <div className="border border-amber-200 rounded-lg p-3 space-y-3 bg-amber-50/30">
                  <div>
                    <span className="text-xs font-medium text-slate-700">Free gift product options</span>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Search or filter by category/subcategory, then add options. These auto-add at ₹0 on Merchant Billing.
                    </p>
                  </div>

                  {(form.freeGiftItems || []).length > 0 && (
                    <ul className="space-y-2">
                      {(form.freeGiftItems || []).map((g) => (
                        <li
                          key={`${g.productMongoId}-${g.optionId}`}
                          className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-2 text-xs"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 truncate">{g.productName}</p>
                            <p className="text-slate-500">{g.optionLabel} · worth ₹{Number(g.catalogPrice || 0).toFixed(0)}</p>
                          </div>
                          <input
                            type="number"
                            min={1}
                            className="w-14 border border-slate-300 rounded px-1 py-1"
                            value={g.quantity ?? 1}
                            onChange={(e) => patchFreeGiftQty(g.productMongoId, g.optionId, e.target.value)}
                            title="Qty free"
                          />
                          <button
                            type="button"
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            onClick={() => removeFreeGift(g.productMongoId, g.optionId)}
                          >
                            <FaTrash size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="search"
                      placeholder="Search products by name / code…"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      value={giftSearch}
                      onChange={(e) => setGiftSearch(e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white"
                        value={giftCategoryId}
                        onChange={(e) => {
                          setGiftCategoryId(e.target.value);
                          setGiftSubcategoryId('');
                        }}
                      >
                        <option value="">All categories</option>
                        {parentCategories.map((c) => (
                          <option key={String(c._id)} value={String(c._id)}>{c.categoryName || c.name}</option>
                        ))}
                      </select>
                      <select
                        className="border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white"
                        value={giftSubcategoryId}
                        disabled={!giftCategoryId}
                        onChange={(e) => setGiftSubcategoryId(e.target.value)}
                      >
                        <option value="">All subcategories</option>
                        {giftSubs.map((s) => (
                          <option key={String(s._id)} value={String(s._id)}>{s.categoryName || s.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
                    {giftLoading ? (
                      <p className="p-3 text-xs text-slate-500 flex items-center gap-2">
                        <FaSpinner className="animate-spin" /> Loading products…
                      </p>
                    ) : giftProducts.length === 0 ? (
                      <p className="p-3 text-xs text-slate-500">No products found. Try another search or category.</p>
                    ) : (
                      giftProducts.map((p) => {
                        const options = Array.isArray(p.productOptions) ? p.productOptions : [];
                        return (
                          <div key={String(p._id)} className="p-2">
                            <p className="text-xs font-semibold text-slate-800 mb-1">{p.productName}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {options.length === 0 ? (
                                <span className="text-[10px] text-slate-400">No options</span>
                              ) : options.map((opt) => {
                                const selected = (form.freeGiftItems || []).some(
                                  (g) => String(g.productMongoId) === String(p._id)
                                    && String(g.optionId) === String(opt._id),
                                );
                                const price = Number(opt.discountPrice || opt.originalPrice || 0) || 0;
                                return (
                                  <button
                                    key={String(opt._id)}
                                    type="button"
                                    title={selected ? 'Click to unselect this option' : 'Click to add as free gift'}
                                    onClick={() => toggleFreeGiftOption(p, opt)}
                                    className={`text-[11px] px-2 py-1 rounded-md border ${
                                      selected
                                        ? 'bg-teal-100 border-teal-300 text-teal-800 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700'
                                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-teal-400'
                                    }`}
                                  >
                                    {optionLabelOf(opt)} · ₹{price.toFixed(0)}
                                    {selected ? ' ✓' : ' +'}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <span className="text-xs font-medium text-slate-600">Reward (what customer saves)</span>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      value={form.reward?.type || 'flat'}
                      onChange={(e) => patchForm('reward.type', e.target.value)}
                    >
                      <option value="flat">Flat ₹ off</option>
                      <option value="percent">% off (with cap)</option>
                    </select>
                    <input
                      type="number"
                      min={0}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      value={form.reward?.value ?? 100}
                      onChange={(e) => patchForm('reward.value', Number(e.target.value) || 0)}
                      placeholder={form.reward?.type === 'percent' ? '%' : '₹'}
                    />
                  </div>
                  {form.reward?.type === 'percent' && (
                    <label className="block">
                      <span className="text-xs text-slate-500">Max discount ₹</span>
                      <input
                        type="number"
                        min={0}
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        value={form.reward?.maxDiscount ?? ''}
                        onChange={(e) => patchForm('reward.maxDiscount', e.target.value === '' ? null : Number(e.target.value))}
                        placeholder="e.g. 100"
                      />
                    </label>
                  )}
                </div>
              )}

              {needsHappyHours && (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <span className="text-xs font-medium text-slate-600">Happy hours timing</span>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs text-slate-500">Starts</span>
                      <input
                        type="time"
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        value={form.happyHours?.start || '16:00'}
                        onChange={(e) => patchForm('happyHours.start', e.target.value)}
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500">Ends</span>
                      <input
                        type="time"
                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        value={form.happyHours?.end || '19:00'}
                        onChange={(e) => patchForm('happyHours.end', e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              )}

              {needsPaymentMethods && (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <span className="text-xs font-medium text-slate-600">Payment method required</span>
                  <div className="flex flex-wrap gap-2">
                    {['UPI', 'Cash', 'Card'].map((m) => {
                      const on = (form.paymentMethods || []).includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => {
                            const cur = form.paymentMethods || [];
                            patchForm(
                              'paymentMethods',
                              on ? cur.filter((x) => x !== m) : [...cur, m],
                            );
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm border ${
                            on ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-300 text-slate-600'
                          }`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.stackWithCoupon}
                  onChange={(e) => patchForm('stackWithCoupon', e.target.checked)}
                />
                <span className="inline-flex items-center">
                  Allow stacking with coupon codes
                  <InfoTip text={TIPS.stackCoupon} placement="top" align="start" />
                </span>
              </label>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!form.oneTimePerCustomer}
                  onChange={(e) => patchForm('oneTimePerCustomer', e.target.checked)}
                />
                <span className="inline-flex items-center">
                  Only valid one time per customer
                  <InfoTip text={TIPS.oneTime} placement="top" align="start" />
                </span>
              </label>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => patchForm('isActive', e.target.checked)}
                />
                Active on Merchant Billing
              </label>

              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-60"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                {editingId ? 'Save changes' : 'Create offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalPopup show={showDeleteConfirm} onClose={cancelDelete}>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">Delete Offer</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Are you sure you want to delete the offer{' '}
            <span className="font-semibold">&quot;{offerPendingDelete?.name || ''}&quot;</span>?
          </p>
          <div className="flex justify-between gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              onClick={cancelDelete}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </ModalPopup>

      {snack && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${
            snack.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-900 text-white'
          }`}
        >
          {snack.message}
        </div>
      )}
    </div>
  );
};

export default PosOffers;
